import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useShallow } from 'zustand/react/shallow';

// --- STORES ---
import {
  usePrintsUiStore,
  usePrintsConfigStore,
  usePrintsOrderStore,
} from '../stores';

// --- HOOKS ---
import { useAddPhotos } from '../hooks/use-add-photos';
import { useAutoOpenPhotoLoader } from '../hooks/use-auto-open-photo-loader';
import { useAlbumLoader } from '../hooks/use-album-loader';
import { useEffectOnce } from '../hooks/use-effect-once';
import { useBeforeUnloadWarning } from '../hooks/use-before-unload-warning';
import { useGa4EventSubscriber } from '../subscribers/use-ga4-event-subscriber';
import { useFocusStyles } from '../hooks/use-focus-styles';
import { useSizesWithPhotos } from '../hooks/use-sizes-with-photos';
import { useSelectedDeliveryOption } from '../hooks/use-selected-delivery-option';
import { useSelectedFinish } from '../hooks/use-selected-finish';
import { useAvailableSizesForSelectedDeliveryAndFinish } from '../hooks/use-available-sizes-for-selected-delivery-and-finish';

// --- EVENTS ---
import UploadManager from '../event-handlers/upload-manager';
import LegacyEventHandler from '../event-handlers/legacy-event-handler';

// --- CORE ---
import { VIEW_MODE } from '../constants';
import { PrintsBuilderUrlsShape, FeaturesShape } from '../prop-types';
import screenAndStyle from '../../_utils/screen-and-style';

// --- COMPONENTS ---
import SummaryChart from './summary-chart';
import UploadProgressBar from '../common/upload-progress-bar';
import FullScreenSpinner from '../../_prism-builder/desktop/notices/full-screen-spinner';
import PrintsBuilderHeader from './prints-builder-header';
import Options from './options';
import BulkQuantityChange from '../common/bulk-quantity-change';
import PrintFinishTabs from '../common/print_finish_tabs';
import DebugControls from '../common/debug-controls';
import MainView from './main-view';
import PhotoUndoRemovalButton from '../common/photo-undo-removal-button';

// --- DUPLICATE DETECTION ---
import { DuplicatePhotoViewToggle } from '../common/duplicate-detection/duplicate-photo-view-toggle';
import { listDuplicatePhotos } from '../common/duplicate-detection/list-duplicate-photos';
import { DuplicatesWarningModal } from '../common/duplicate-detection/duplicates-warning-modal';

// --- MODALS ---
import PhotoRemoveConfirmation from '../common/photo-remove-confirmation';
import AdvancedEditorWrapper from '../common/advanced-editor/advanced-editor-wrapper';
import InvalidSizesModal from '../common/invalid-sizes-modal';
import InvalidDeliveryOptionModal from '../common/invalid-delivery-option-modal';
import PricingChartPopup from './pricing-chart-popup';
import PhotoRemoveAllButton from '../common/photo-remove-all-button';

const DesktopPrintsBuilder = ({
  urls,
  features,
  uploadFromGalleryListenerUrl,
}) => {
  const { upload_from_album: uploadFromAlbumUrl } = urls;
  const { printsBuilderDuplicateDetectionDisabled, debugEnabled } = features;
  const duplicatePhotoDetectionEnabled =
    !printsBuilderDuplicateDetectionDisabled;

  // --- STATE ---
  const ready = usePrintsUiStore((state) => state.ready);
  const currentlyLoading = usePrintsUiStore((state) => state.currentlyLoading);
  const currentView = usePrintsUiStore((state) => state.currentView);
  const isSaving = usePrintsUiStore((state) => state.isSaving);

  const allSizes = usePrintsConfigStore((state) => state.allSizes);
  const allFinishes = usePrintsConfigStore((state) => state.allFinishes);
  const allDeliveryOptions = usePrintsConfigStore(
    (state) => state.allDeliveryOptions,
  );
  const finishesByDeliveryOption = usePrintsConfigStore(
    (state) => state.availableFinishesByDeliveryOption,
  );

  const photos = usePrintsOrderStore((state) => state.photos);

  // --- ACTIONS ---
  const { loadInitialData } = usePrintsConfigStore(
    useShallow((state) => ({
      loadInitialData: state.loadInitialData,
    })),
  );

  const { handleDeliveryOptionOrFinishChanged } = usePrintsUiStore(
    useShallow((state) => ({
      handleDeliveryOptionOrFinishChanged:
        state.handleDeliveryOptionOrFinishChanged,
    })),
  );

  // --- HOOKS ---
  const addPhotos = useAddPhotos();
  const sizesForCurrentFinishWithPhotos = useSizesWithPhotos(true);
  const allSizesWithAllPhotos = useSizesWithPhotos(false);
  const deliveryOption = useSelectedDeliveryOption();
  const finish = useSelectedFinish();
  const availableSizes = useAvailableSizesForSelectedDeliveryAndFinish();
  const { hasAlbumImportPending } = useAlbumLoader({
    ready,
    uploadFromGalleryListenerUrl,
    uploadFromAlbumUrl,
  }); // Hook to load photos from a gallery URL or localStorage
  useBeforeUnloadWarning(); // Handle showing a warning to the user if they try to navigate away
  useGa4EventSubscriber(); // Listen for GA4 events
  useFocusStyles(); // Apply focus-related accessibility styles on the document body

  // --- SELECTORS ---
  const hasPhotos = useMemo(
    () =>
      sizesForCurrentFinishWithPhotos.some((size) => size.photos.length > 0),
    [sizesForCurrentFinishWithPhotos],
  );
  const { photosWithDuplicates, totalDuplicatesCount } = useMemo(() => {
    if (!duplicatePhotoDetectionEnabled) {
      return { photosWithDuplicates: [], totalDuplicatesCount: 0 };
    }
    return listDuplicatePhotos(sizesForCurrentFinishWithPhotos);
  }, [duplicatePhotoDetectionEnabled, sizesForCurrentFinishWithPhotos]);
  const editingDisabled = !!currentlyLoading || isSaving;
  useAutoOpenPhotoLoader({
    hasPhotos,
    uploadFromGalleryListenerUrl,
    addPhotos,
    hasAlbumImportPending,
  });

  // --- EFFECTS ---

  // Trigger data load on component mount
  useEffectOnce(
    ([urlsDep, featuresDep, loadInitialDataDep]) => {
      loadInitialDataDep({
        urls: urlsDep,
        features: featuresDep,
      });
    },
    [urls, features, loadInitialData],
    () => true,
  );

  // --- RENDER ---

  return (
    <div
      className={classNames('prints-builder__grid desktop', {
        'is-touch': screenAndStyle.isTouch(),
        'expanded-sidebar': features.newPrintsBuilderDeliveryAndFinishIcons,
      })}>
      <UploadManager />
      <LegacyEventHandler />
      <PrintsBuilderHeader />

      <main className="prints-builder__list d-flex flex-column position-relative">
        {features.allowMultiplePrintFinishesPerOrder && (
          <PrintFinishTabs finishes={allFinishes} selectedFinish={finish} />
        )}
        <div className="row grey-zone flex-shrink-0">
          <div
            className={
              duplicatePhotoDetectionEnabled || debugEnabled
                ? 'col-10'
                : 'col-11'
            }>
            <BulkQuantityChange
              availableSizes={sizesForCurrentFinishWithPhotos} // this needs photos for each size
              photos={photos} // only used for rootIds
              deliveryOption={deliveryOption || {}}
              disabled={!hasPhotos || !!currentlyLoading}
            />
          </div>
          <div
            className={classNames(
              'pr-0 d-flex flex-column',
              `${duplicatePhotoDetectionEnabled
                ? 'col-2 pl-3 justify-content-around'
                : 'col-1 pl-0 justify-content-end'
              }`,
            )}>
            {debugEnabled && <DebugControls />}
            <PhotoRemoveAllButton
              disabled={!hasPhotos || editingDisabled}
              finish={finish}
              multipleFinishesPerOrder={
                features.allowMultiplePrintFinishesPerOrder
              }
            />
            <PhotoUndoRemovalButton
              disabled={editingDisabled}
            />
            {duplicatePhotoDetectionEnabled && (
              <DuplicatePhotoViewToggle
                totalDuplicatesCount={totalDuplicatesCount}
                duplicatePhotoViewActive={currentView === VIEW_MODE.DUPLICATES}
              />
            )}
          </div>
        </div>
        <UploadProgressBar {...currentlyLoading} />
        <MainView
          addPhotos={addPhotos}
          availableSizes={availableSizes}
          currentView={currentView}
          currentlyLoading={currentlyLoading}
          deliveryOption={deliveryOption}
          editingDisabled={editingDisabled}
          hasPhotos={hasPhotos}
          isSaving={isSaving}
          photosWithDuplicates={photosWithDuplicates}
          ready={ready}
          sizesByFinish={sizesForCurrentFinishWithPhotos}
          totalDuplicatesCount={totalDuplicatesCount}
        />
      </main>

      <aside className="prints-builder__side-bar d-flex flex-column grey-zone">
        <Options
          availableSizes={availableSizes}
          deliveryOptions={allDeliveryOptions}
          deliveryOption={deliveryOption || {}}
          finishesByDeliveryOption={finishesByDeliveryOption}
          finishes={allFinishes}
          finish={finish || {}}
          showAllPricesLink={features.pricesPopup}
          hidePrintFinishes={features.allowMultiplePrintFinishesPerOrder}
          newPrintsBuilderDeliveryAndFinishIcons={
            features.newPrintsBuilderDeliveryAndFinishIcons
          }
          onChange={handleDeliveryOptionOrFinishChanged}
          disabled={editingDisabled}
        />
        <SummaryChart
          className="flex-grow-1"
          sizes={allSizesWithAllPhotos}
          deliveryOption={deliveryOption || {}}
        />
        <button
          className={`btn btn-outline-secondary add-photos ${features.newPrintsBuilderDeliveryAndFinishIcons
            ? 'align-self-center w-75'
            : ''
            }`}
          role="button"
          onClick={addPhotos}
          disabled={!ready || editingDisabled}>
          Add {hasPhotos ? 'More' : ''} Photos
        </button>
      </aside>

      <FullScreenSpinner show={!ready} />
      <PhotoRemoveConfirmation />
      <DuplicatesWarningModal />
      <InvalidSizesModal />
      <InvalidDeliveryOptionModal />
      <AdvancedEditorWrapper />
      {features.pricesPopup && ready && (
        <PricingChartPopup
          products={allSizes}
          finishes={allFinishes}
          deliveryOptions={allDeliveryOptions}
        />
      )}
    </div>
  );
};

DesktopPrintsBuilder.propTypes = {
  urls: PrintsBuilderUrlsShape.isRequired,
  features: FeaturesShape.isRequired,
  uploadFromGalleryListenerUrl: PropTypes.string,
};

export default DesktopPrintsBuilder;
